import { computed, nextTick, onMounted, onUnmounted, ref, Ref } from 'vue';
import {
  CreateFileFolderModel,
  DeleteInvalidFileRequestModel,
  FileImportProgressModel,
  RemoveFileModel,
  TaskFormDataModel,
  TaskModel,
} from '../models';
import { TaskFileModel, TaskImportFileModel, UserModel } from '@/components/task-forms/models';
import { FileUploadCallbackFunctionType } from 'tir-pm-texteditor-file';
import {
  FileUploadStatusEnum,
  VideoModel,
  ImportVideoModel,
  AbstractFileModel,
  FileManager,
} from 'tir-pm-file-upload-manager';
import { CurrentUser } from 'tir-pm-user-core';
import { AdapterService } from '../adapter';
import { mapToEditorFile } from '../mappers';
import { FileFolderTypeEnum } from '@/enums';

type UseTaskFilesParams = {
  /**Текущая задача */
  currentTask: Ref<TaskModel | undefined>;
  /**Данные формы */
  formData: Ref<TaskFormDataModel>;
  /**Id проекта */
  projectId: Ref<number>;
  /**Id очереди */
  queueId: Ref<string>;
};

export const useTaskFiles = (params: UseTaskFilesParams) => {
  const adapter = new AdapterService();
  /**Загрузка при прикреплении файлов */
  const isLoadingAttachFiles = ref(false);
  /**Загрузка при отмене загрузки файлов */
  const isLoadingAbortFiles = ref(false);

  /**Загружаемыые файлы */
  const uploadingFiles = computed(() => {
    const filtredFiles = FileManager.ActiveUploads.value.filter((file) => {
      return (
        file.Location &&
        params.formData.value.FolderId &&
        file.Location.FolderId === params.formData.value.FolderId &&
        file.Status === FileUploadStatusEnum.Uploading &&
        (file.isFileModel() || file.isVideoModel())
      );
    });

    return filtredFiles.map((file) => {
      return new TaskFileModel({
        Id: file.FileId,
        Url: file.Url,
        VideoPreviewUrl: file.isVideoModel() ? file.ThumbnailUrl : undefined,
        Name: file.Name,
        Size: file.Size,
        DateUpload: file.DateCreate,
        User: new UserModel({
          Id: CurrentUser.Id,
          FirstName: CurrentUser.FirstName,
          LastName: CurrentUser.LastName,
          MiddleName: CurrentUser.MiddleName,
          AvatarColor: CurrentUser.AvatarColor,
          ImageUrl: CurrentUser.Foto,
        }),
        UploadProgress: file.Progress,
        IsProccessing: file.Status === FileUploadStatusEnum.Processing,
        IsSendingLastChunk: (file.isFileModel() || file.isVideoModel()) && file.IsSendingLastChunk,
      });
    });
  });
  /**Импортируемые файлы */
  const importFiles = computed(() => {
    return params.formData.value.Files.filter((file) => file.UploadProgress !== undefined);
  });
  /**Отображаемые файлы */
  const visibleFiles = computed(() => {
    const formDataFiles = params.formData.value.Files.filter(
      (file) => !uploadingFiles.value.some((_uploadFile) => file.Id === _uploadFile.Id),
    );

    return [...formDataFiles, ...uploadingFiles.value];
  });
  /**Файлы для редактора */
  const editorFiles = computed(() => visibleFiles.value.map(mapToEditorFile));

  /**
   * Вставить файл из описания
   * @param file Файл
   * @param pasteSlotElement Функция для вставки в редактор
   * @returns
   */
  const attachFileFromDescription: FileUploadCallbackFunctionType = async (
    file: File,
    pasteSlotElement: (slotId: string | number) => void,
  ) => {
    isLoadingAttachFiles.value = true;
    // Если файл добавляется в режиме создания задачи и отсутствует идентификатор папки с файлами
    if (!params.currentTask.value?.Id && !params.formData.value.FolderId) {
      const resp = await adapter.createFolderWithoutEntity(
        new CreateFileFolderModel({
          ProjectId: params.projectId.value,
          Type: FileFolderTypeEnum.Task,
        }),
      );
      if (!resp.IsSuccess || !resp.Value) return;
      params.formData.value.FolderId = resp.Value;
    }
    const resp = await FileManager.upload({
      Item: { File: file, CompressSize: 5 },
      Location: {
        FolderId: params.formData.value.FolderId,
        TaskId: params.currentTask.value?.Id,
        QueueTaskId: params.queueId.value,
      },
    });

    if (resp.IsSuccess) {
      pasteSlotElement(resp.Value.FileId);
    }
    isLoadingAttachFiles.value = false;
  };

  /**Начать импорт файлов */
  const startImportFiles = async () => {
    if (!params.formData.value.FilesToImport.length) return;
    if (!params.formData.value.FolderId) {
      const resp = await adapter.createFolderWithoutEntity(
        new CreateFileFolderModel({
          ProjectId: params.projectId.value,
          Type: FileFolderTypeEnum.Task,
        }),
      );
      if (!resp.IsSuccess || !resp.Value) return;
      params.formData.value.FolderId = resp.Value;
    }
    console.log(FileManager.importBatch);

    await FileManager.importBatch({
      Location: {
        FolderId: params.formData.value.FolderId,
        TaskId: params.currentTask.value?.Id,
      },
      Items: params.formData.value.FilesToImport.map((file) => ({
        FileName: file.Name,
        FileSize: file.Size,
        FileUrl: file.Url,
      })),
    });
  };

  /**Удалить файл */
  const removeFile = async (fileId: number) => {
    const isUploadFile = uploadingFiles.value.some((file) => file.Id === fileId);

    if (isUploadFile) {
      FileManager.cancel({ FileId: fileId });
      return;
    }

    if (!params.currentTask.value?.Id) {
      const resp = await adapter.removeFileBeforeTaskCreation(
        new DeleteInvalidFileRequestModel({
          FileId: fileId,
          FolderId: params.formData.value?.FolderId,
        }),
      );
      if (resp.IsSuccess) {
        params.formData.value.Files = params.formData.value.Files.filter(
          (file) => file.Id !== fileId,
        );
        if (!params.formData.value.Files.length) {
          delete params.formData.value.FolderId;
        }
        params.formData.value.Description = removeFileSpans(
          params.formData.value.Description,
          fileId,
        );
        if (
          !params.formData.value.Description.length ||
          params.formData.value.Description === '<div><br></div>'
        ) {
          delete params.formData.value.Description;
        }
      }
    } else {
      const resp = await adapter.removeFile(
        new RemoveFileModel({
          TaskId: params.currentTask.value?.Id,
          FileId: fileId,
        }),
      );
      if (resp.IsSuccess) {
        params.currentTask.value.Files = params.currentTask.value.Files.filter(
          (file) => file.Id !== fileId,
        );
        params.formData.value.Files = [...params.currentTask.value.Files];
      }
    }
  };
  /**Удаление span файла */
  const removeFileSpans = (description: string, fileId: number) => {
    if (!description) return description;

    const regex = new RegExp(`<span[^>]*data-file-id="${fileId}"[^>]*><\\/span>`, 'g');

    return description.replace(regex, '');
  };

  /**
   * * При начале обработки файла
   * @param fileOrVideo
   * @returns
   */
  const onStartProccessingFile = (fileOrVideo: VideoModel | ImportVideoModel) => {
    if (
      !params.formData.value.FolderId ||
      params.formData.value.Files.some((_file) => _file.Id === fileOrVideo.FileId)
    )
      return;
    if (fileOrVideo.Location.FolderId !== params.formData.value.FolderId) return;

    params.formData.value.Files.push(
      new TaskFileModel({
        Id: fileOrVideo.FileId,
        Url: fileOrVideo.Url,
        VideoPreviewUrl: fileOrVideo.isVideoModel() ? fileOrVideo.ThumbnailUrl : undefined,
        Name: fileOrVideo.Name,
        Size: fileOrVideo.Size,
        DateUpload: fileOrVideo.DateCreate,
        IsProccessing: true,
        UploadProgress: 100,
        User: new UserModel({
          Id: CurrentUser.Id,
          FirstName: CurrentUser.FirstName,
          LastName: CurrentUser.LastName,
          MiddleName: CurrentUser.MiddleName,
          AvatarColor: CurrentUser.AvatarColor,
          ImageUrl: CurrentUser.Foto,
        }),
      }),
    );
  };

  /**
   * * Обработчик успешного добавления файла из FileManager
   * @param file Файл
   */
  const onCompleteAddFileFromFileManager = (file: AbstractFileModel) => {
    if (
      file.Location.FolderId !== params.formData.value.FolderId ||
      params.formData.value.Files.some((_file) => _file.Id === file.FileId)
    )
      return;

    params.formData.value.Files.push(
      new TaskFileModel({
        Id: file.FileId,
        Url: file.Url,
        VideoPreviewUrl: file.isVideoModel() ? file.ThumbnailUrl : undefined,
        Name: file.Name,
        Size: file.Size,
        DateUpload: file.DateCreate,
        User: new UserModel({
          Id: CurrentUser.Id,
          FirstName: CurrentUser.FirstName,
          LastName: CurrentUser.LastName,
          MiddleName: CurrentUser.MiddleName,
          AvatarColor: CurrentUser.AvatarColor,
          ImageUrl: CurrentUser.Foto,
        }),
      }),
    );
  };

  /**
   * * Обработчик успешного добавления файла
   * @param fileId Id файла
   */
  const onCompleteAddFile = (fileId: number) => {
    const file = params.formData.value.Files.find((file) => file.Id === fileId);
    if (file) {
      file.UploadProgress = undefined;
      file.IsProccessing = false;
    }
  };

  /**
   * Обработчик события прогресса импорта файла
   * @param event
   * @returns
   */
  const onProgressImportFile = (event: FileImportProgressModel) => {
    const file = params.formData.value.Files.find((file) => file.Id === event.FileId);
    if (!file) return;
    file.UploadProgress = event.Progress;
    file.UploadId = file.UploadId;
  };

  const checkIsVideo = (fileName: string): boolean => {
    const ext = fileName.split('.').pop().toLowerCase();
    return ['avi', 'mp4', 'mkv', 'mov', 'flv'].includes(ext);
  };

  const abortFiles = async (files: Array<TaskFileModel>) => {
    const abortedFileIds = new Set<number>();
    const promises = files.map((file) => {
      if (
        FileManager.ActiveUploads.value.some(
          (activeFile) => file.Id === activeFile.FileId && activeFile.Progress !== 100,
        )
      ) {
        return FileManager.cancel({ FileId: file.Id }).then((resp) => {
          if (resp.IsSuccess) abortedFileIds.add(file.Id);
        });
      }
      if (file.UploadId) {
        const abortPromise = checkIsVideo(file.Name)
          ? adapter.abortUploadVideo(file.UploadId)
          : adapter.abortUploadFile(file);
        return abortPromise.then((resp) => {
          if (resp.IsSuccess) abortedFileIds.add(file.Id);
        });
      }
      return Promise.resolve(); // Для файлов без условий отмены
    });
    isLoadingAbortFiles.value = true;
    await Promise.all(promises);
    isLoadingAbortFiles.value = false;
    params.formData.value.Files = params.formData.value.Files.filter(
      (file) => !abortedFileIds.has(file.Id),
    );
  };

  onMounted(() => {
    FileManager.StartProcessingEvent.on(onStartProccessingFile);
    FileManager.CompleteEvent.on(onCompleteAddFileFromFileManager);
    adapter.OnCompleteAddFileEvent.on(onCompleteAddFile);
    adapter.FileImportProgressEvent.on(onProgressImportFile);
  });

  onUnmounted(() => {
    FileManager.StartProcessingEvent.removeListener(onStartProccessingFile);
    FileManager.CompleteEvent.removeListener(onCompleteAddFileFromFileManager);
    adapter.OnCompleteAddFileEvent.removeListener(onCompleteAddFile);
    adapter.FileImportProgressEvent.removeListener(onProgressImportFile);
  });

  return {
    /**Загрузка при прикреплении файлов */
    isLoadingAttachFiles,
    /**Загружаемыые файлы */
    uploadingFiles,
    /**Импортируемые файлы */
    importFiles,
    /**Отображаемые файлы */
    visibleFiles,
    /**Файлы для редактора */
    editorFiles,
    /**
     * Вставить файл из описания
     * @param file Файл
     * @param pasteSlotElement Функция для вставки в редактор
     * @returns
     */
    attachFileFromDescription,
    /**Удалить файл */
    removeFile,
    /**Начать импорт файлов */
    startImportFiles,

    abortFiles,

    isLoadingAbortFiles,
  };
};
